/* BLANC SPACE CUSTOM — presentation layer for Loox's read-only Storefront API. */

class LooxApiReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true' || !this.dataset.storeId) return;

    this.dataset.initialized = 'true';
    this.baseUrl = `https://storefront-api.loox.io/storefront/v1/store/${encodeURIComponent(this.dataset.storeId)}`;
    this.productId = this.dataset.productId;
    this.pageSize = Number.parseInt(this.dataset.pageSize, 10) || 6;
    this.maxReviews = Math.min(100, Number.parseInt(this.dataset.maxReviews, 10) || 100);
    this.locale = this.dataset.locale || 'en';
    this.reviewPool = [];
    this.viewerItems = [];
    this.visibleCount = 0;
    this.viewerIndex = 0;
    this.viewerBuilt = false;
    this.requestController = null;
    this.initialLoadPromise = null;
    this.reviewsLoaded = false;
    this.viewerScrollTimer = null;
    this.lastViewerTrigger = null;

    this.grid = this.querySelector('[data-review-grid]');
    this.status = this.querySelector('[data-review-status]');
    this.empty = this.querySelector('[data-review-empty]');
    this.moreButton = this.querySelector('[data-review-more]');
    this.resultLabel = this.querySelector('[data-result-label]');
    this.summaryAvatars = this.querySelector('[data-review-summary-avatars]');
    this.lightbox = this.querySelector('[data-review-lightbox]');
    this.viewerRail = this.querySelector('[data-review-viewer-rail]');
    this.viewerPlayer = this.querySelector('[data-review-viewer-player]');
    this.viewerTrack = this.querySelector('[data-review-viewer-track]');
    this.viewerPrev = this.querySelector('[data-review-viewer-prev]');
    this.viewerNext = this.querySelector('[data-review-viewer-next]');
    this.videoControls = this.querySelector('[data-review-video-controls]');
    this.muteButton = this.querySelector('[data-review-viewer-mute]');
    this.playButton = this.querySelector('[data-review-viewer-play]');

    this.addEventListener('click', this.handleClick);
    this.lightbox?.addEventListener('close', this.clearLightbox);
    this.lightbox?.addEventListener('click', this.handleLightboxBackdrop);
    this.viewerTrack?.addEventListener('scroll', this.handleViewerScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.cardResizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => this.layoutReviewCard(entry.target));
      });
    }

    if ('IntersectionObserver' in window) {
      this.highlightObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => this.classList.toggle('is-highlight-visible', entry.isIntersecting));
      }, { threshold: 0.12 });
      this.highlightObserver.observe(this);
    }

    // The aggregate count also updates the rating link above the fold, so load
    // this small cached response immediately while the larger feed stays lazy.
    this.loadBottomline();

    // The rating link's avatar cluster is also above the fold, so it gets its
    // own single-page media fetch instead of waiting for the lazy feed.
    this.ratingAvatarButtons = Array.from(document.querySelectorAll('[data-bs-review-avatars]'));
    if (this.ratingAvatarButtons.length) {
      document.addEventListener('click', this.handleRatingAvatarsClick);
      this.loadRatingAvatars();
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.loadInitial();
      }, { rootMargin: '500px 0px' });
      this.observer.observe(this);
    } else {
      this.loadInitial();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleRatingAvatarsClick);
    this.observer?.disconnect();
    this.highlightObserver?.disconnect();
    this.cardResizeObserver?.disconnect();
    this.inlineTeaserObserver?.disconnect();
    this.requestController?.abort();
    window.clearTimeout(this.viewerScrollTimer);
    this.removeEventListener('click', this.handleClick);
    this.lightbox?.removeEventListener('close', this.clearLightbox);
    this.lightbox?.removeEventListener('click', this.handleLightboxBackdrop);
    this.viewerTrack?.removeEventListener('scroll', this.handleViewerScroll);
  }

  handleClick = (event) => {
    if (event.target.closest('[data-review-more]')) {
      this.loadReviews({ reset: false });
      return;
    }

    const mediaButton = event.target.closest('[data-review-media]');
    if (mediaButton) {
      this.openViewer(mediaButton.dataset.viewerKey, mediaButton);
      return;
    }

    const viewerSelect = event.target.closest('[data-review-viewer-select]');
    if (viewerSelect) {
      this.selectViewer(Number.parseInt(viewerSelect.dataset.reviewViewerSelect, 10));
      return;
    }

    if (event.target.closest('[data-review-viewer-prev]')) {
      this.selectViewer(this.viewerIndex - 1);
      return;
    }

    if (event.target.closest('[data-review-viewer-next]')) {
      this.selectViewer(this.viewerIndex + 1);
      return;
    }

    if (event.target.closest('[data-review-viewer-mute]')) {
      this.toggleViewerMute();
      return;
    }

    if (event.target.closest('[data-review-viewer-play]')) {
      this.toggleViewerPlayback();
      return;
    }

    if (event.target.closest('[data-lightbox-close]')) this.lightbox?.close();
  };

  handleLightboxBackdrop = (event) => {
    if (event.target === this.lightbox) this.lightbox.close();
  };

  handleViewerScroll = () => {
    window.clearTimeout(this.viewerScrollTimer);
    this.viewerScrollTimer = window.setTimeout(() => {
      if (!this.viewerTrack?.clientWidth) return;
      const index = Math.round(this.viewerTrack.scrollLeft / this.viewerTrack.clientWidth);
      if (index !== this.viewerIndex) this.selectViewer(index, { scroll: false });
    }, 80);
  };

  clearLightbox = () => {
    this.pauseViewerVideos();
    document.documentElement.classList.remove('loox-review-viewer-open');
    this.lastViewerTrigger?.focus({ preventScroll: true });
    this.lastViewerTrigger = null;
  };

  async loadInitial() {
    if (this.reviewsLoaded) return;
    if (!this.initialLoadPromise) {
      this.initialLoadPromise = this.loadReviews({ reset: true })
        .then((loaded) => {
          this.reviewsLoaded = loaded;
          return loaded;
        })
        .finally(() => { this.initialLoadPromise = null; });
    }
    await this.initialLoadPromise;
  }

  async loadBottomline() {
    try {
      const response = await fetch(`${this.baseUrl}/product-reviews/bottomline`);
      if (!response.ok) throw new Error(`Loox bottomline request failed (${response.status})`);
      const bottomline = await response.json();
      this.renderBottomline(bottomline);
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[Blanc Space] Loox review summary unavailable.', error);
    }
  }

  async loadReviews({ reset }) {
    if (!reset) {
      this.renderNextPage();
      return;
    }

    this.requestController?.abort();
    this.requestController = new AbortController();
    this.reviewPool = [];
    this.viewerItems = [];
    this.viewerBuilt = false;
    this.visibleCount = 0;
    this.cardResizeObserver?.disconnect();
    this.grid.replaceChildren();
    this.viewerRail?.replaceChildren();
    this.viewerTrack?.replaceChildren();
    this.grid.hidden = true;
    this.empty.hidden = true;
    this.moreButton.hidden = true;
    this.setLoading(true, 'Loading customer reviews…');

    try {
      const [productPayload, storePayload] = await Promise.all([
        this.fetchMediaReviewPages(this.buildReviewsUrl({ productId: this.productId })),
        this.fetchMediaReviewPages(this.buildReviewsUrl()),
      ]);

      const productReviews = productPayload.reviews;
      const productReviewIds = new Set(productReviews.map((review) => String(review.id)));
      const otherReviews = storePayload.reviews.filter((review) => {
        if (productReviewIds.has(String(review.id))) return false;
        return String(review.product?.id || '') !== String(this.productId);
      });

      // Product relevance wins first; Loox media is then prioritised within the
      // product and aggregate groups. The client-side pool deliberately stops
      // at 100 even though the aggregate total remains visible to shoppers.
      this.reviewPool = [...productReviews, ...otherReviews].slice(0, this.maxReviews);
      this.viewerItems = this.reviewPool.flatMap((review) => (Array.isArray(review.media) ? review.media : []).map((media, mediaIndex) => ({
        review,
        media,
        mediaIndex,
        key: `${review.id}:${mediaIndex}`,
      }))).filter((item) => item.media?.url);
      this.syncSharedViewer();
      this.renderSummaryAvatars();
      this.renderRatingAvatars();
      this.resultLabel.textContent = this.getResultLabel(storePayload.pagination?.total ?? this.reviewPool.length);
      this.setLoading(false);
      this.renderNextPage();
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;
      console.error('[Blanc Space] Loox reviews unavailable.', error);
      this.setLoading(false);
      this.empty.hidden = false;
      this.empty.querySelector('strong').textContent = 'Reviews are taking a moment';
      this.empty.querySelector('span').textContent = 'Please refresh the page or try again shortly.';
      this.resultLabel.textContent = 'Reviews unavailable';
      return false;
    }
  }

  async fetchReviews(url) {
    const response = await fetch(url, { signal: this.requestController?.signal });
    if (!response.ok) throw new Error(`Loox reviews request failed (${response.status})`);
    const payload = await response.json();
    return {
      reviews: Array.isArray(payload.reviews) ? payload.reviews : [],
      pagination: payload.pagination || {},
    };
  }

  async fetchMediaReviewPages(initialUrl) {
    const reviews = [];
    const seenIds = new Set();
    let nextUrl = initialUrl;
    let pagination = {};
    let pageCount = 0;

    // Loox caps a response at 100 entries. Continue briefly when text-only
    // reviews consume those slots so the storefront can still build a full
    // 100-item media pool without an unbounded request chain.
    while (nextUrl && reviews.length < this.maxReviews && pageCount < 3) {
      const payload = await this.fetchReviews(nextUrl);
      pagination = { ...payload.pagination, total: pagination.total ?? payload.pagination.total };
      payload.reviews.forEach((review) => {
        if (!Array.isArray(review.media) || review.media.length === 0) return;
        const id = String(review.id);
        if (seenIds.has(id)) return;
        seenIds.add(id);
        reviews.push(review);
      });
      nextUrl = payload.pagination?.hasMore ? payload.pagination.nextPageUrl : '';
      pageCount += 1;
    }

    return { reviews: reviews.slice(0, this.maxReviews), pagination };
  }

  buildReviewsUrl({ productId } = {}) {
    const url = new URL(`${this.baseUrl}/product-reviews`);
    if (productId) url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(this.maxReviews));
    url.searchParams.set('locale', this.locale);

    url.searchParams.set('sort', 'featured');
    url.searchParams.set('direction', 'desc');
    return url.toString();
  }

  getSharedViewerItems() {
    return this.viewerItems.map((item) => ({
      key: item.key,
      reviewId: String(item.review.id),
      type: item.media.type === 'video' ? 'video' : 'image',
      url: item.media.type === 'video' ? this.getPlayableVideoUrl(item.media.url) : item.media.url,
      thumbnailUrl: item.media.type === 'video' ? (item.media.thumbnailUrl || '') : item.media.url,
      reviewerName: item.review.reviewer?.name || 'Blanc Space customer',
      verified: Boolean(item.review.verified),
      date: item.review.date ? this.formatDate(item.review.date) : '',
      rating: this.clampRating(item.review.rating),
      body: item.review.body || '',
      highlights: this.getReviewHighlights(item.review),
      product: item.review.product ? {
        id: String(item.review.product.id || ''),
        name: item.review.product.name || 'a Blanc Space product',
        url: item.review.product.url || '',
        imageUrl: item.review.product.imageUrl || '',
        isCurrent: String(item.review.product.id || '') === String(this.productId),
      } : null,
    }));
  }

  syncSharedViewer() {
    const items = this.getSharedViewerItems();
    const sharedViewer = document.querySelector('video-feedback[data-review-continuation]');
    sharedViewer?.setReviewItems?.(items, this);
    document.dispatchEvent(new CustomEvent('blancspace:loox-review-media-ready', {
      detail: { items, source: this },
    }));
  }

  getPlayableVideoUrl(url) {
    try {
      const parsed = new URL(url);
      if (!['iframe.videodelivery.net', 'videodelivery.net'].includes(parsed.hostname)) return url;
      const videoId = parsed.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://videodelivery.net/${videoId}/downloads/default.mp4` : url;
    } catch (_) {
      return url;
    }
  }

  renderNextPage() {
    const nextVisibleCount = Math.min(this.visibleCount + this.pageSize, this.reviewPool.length);
    this.reviewPool.slice(this.visibleCount, nextVisibleCount).forEach((review) => {
      const card = this.createReviewCard(review);
      this.grid.append(card);
      this.cardResizeObserver?.observe(card);
      requestAnimationFrame(() => this.layoutReviewCard(card));
    });
    this.visibleCount = nextVisibleCount;
    this.grid.hidden = this.visibleCount === 0;
    this.empty.hidden = this.visibleCount !== 0;
    this.renderEmptyState();
    this.moreButton.hidden = this.visibleCount >= this.reviewPool.length;
    this.initInlineVideoTeaser();
  }

  /* First video review in the grid loops muted while visible, like the
     video-feedback rail's first circle — user testing showed nobody realised
     the review cards held videos. One video only, source attached just
     before it scrolls into view, released again when it leaves. Clicks still
     open the shared viewer (the <video> is pointer-inert). */
  initInlineVideoTeaser() {
    if (this.inlineTeaserButton?.isConnected) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const button = this.grid?.querySelector('[data-review-media][data-media-type="video"][data-video-url]');
    if (!button) return;

    this.inlineTeaserButton = button;
    this.inlineTeaserObserver?.disconnect();

    const video = document.createElement('video');
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.preload = 'none';
    video.className = 'loox-api-review-card__inline-video';
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    video.addEventListener('playing', () => button.classList.add('is-video-playing'), { once: true });
    video.addEventListener('error', () => {
      // Broken stream: drop back to the static thumbnail permanently.
      this.inlineTeaserObserver?.disconnect();
      video.remove();
      button.classList.remove('is-video-playing');
    });
    button.append(video);

    this.inlineTeaserObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!video.src) video.src = button.dataset.videoUrl;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '200px 0px' });
    this.inlineTeaserObserver.observe(button);
  }

  renderBottomline(bottomline) {
    const total = Number(bottomline.totalReviews) || 0;
    const average = Number(bottomline.averageRating) || 0;
    const averageNode = this.querySelector('[data-review-average]');
    const countNode = this.querySelector('[data-review-count]');
    const starsNode = this.querySelector('[data-review-stars]');

    if (averageNode) averageNode.textContent = average.toFixed(1);
    if (countNode) countNode.textContent = `${this.formatNumber(total)} ${total === 1 ? 'review' : 'reviews'}`;
    if (starsNode) starsNode.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars`);

    document.querySelectorAll('[data-bs-review-average]').forEach((node) => {
      node.textContent = average.toFixed(1);
    });
    document.querySelectorAll('[data-bs-review-count]').forEach((node) => {
      node.textContent = this.formatNumber(total);
    });
    document.querySelectorAll('[data-bs-review-stars]').forEach((node) => {
      node.setAttribute('aria-label', `Rated ${average.toFixed(1)} out of 5`);
    });
    document.querySelectorAll('[data-bs-review-star-fill]').forEach((node) => {
      node.style.width = `${Math.max(0, Math.min(100, average * 20))}%`;
    });

    const distribution = bottomline.ratingDistribution || {};
    this.querySelectorAll('[data-rating-row]').forEach((row) => {
      const ratings = (row.dataset.ratings || '').split(',');
      const count = ratings.reduce((sum, rating) => sum + (Number(distribution[rating.trim()]) || 0), 0);
      const percentage = total > 0 ? (count / total) * 100 : 0;
      row.querySelector('[data-rating-fill]').style.width = `${percentage}%`;
      row.querySelector('[data-rating-count]').textContent = this.formatNumber(count);
      row.setAttribute('aria-label', `${row.dataset.sentimentLabel}, ${count}`);
    });
  }

  handleRatingAvatarsClick = (event) => {
    const button = event.target.closest?.('[data-bs-review-avatars]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    this.openViewerFromRatingAvatars(button);
  };

  async openViewerFromRatingAvatars(button) {
    if (!this.viewerItems.length) {
      button.setAttribute('aria-busy', 'true');
      this.observer?.disconnect();
      try {
        await this.loadInitial();
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
    // The pool is product-first ordered, so item 0 is this product's first media review when one exists.
    const firstItem = this.viewerItems[0];
    if (firstItem) {
      this.openViewer(firstItem.key, button);
      return;
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getMediaThumbUrl(media) {
    if (!media) return '';
    return media.type === 'video' ? (media.thumbnailUrl || '') : (media.url || '');
  }

  /* The Loox API hands back `_orig` assets (~700KB+); the suffix-less variant
     (~375x500, ~30KB) is plenty for avatars, card thumbs and viewer-rail
     circles. The full-size lightbox slide keeps `_orig`. onerror falls back
     for any review that lacks the base variant. */
  smallLooxUrl(image, url) {
    const small = /images\.loox\.io\//.test(url || '') ? url.replace(/_orig(\.[a-z]+)(\?|$)/i, '$1$2') : url;
    if (image && small !== url) image.addEventListener('error', () => { image.src = url; }, { once: true });
    return small;
  }

  async loadRatingAvatars() {
    if (!this.ratingAvatarButtons?.length) return;
    try {
      const url = new URL(`${this.baseUrl}/product-reviews`);
      if (this.productId) url.searchParams.set('product_id', this.productId);
      url.searchParams.set('limit', '12');
      url.searchParams.set('locale', this.locale);
      url.searchParams.set('sort', 'featured');
      url.searchParams.set('direction', 'desc');
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Loox avatar request failed (${response.status})`);
      const payload = await response.json();
      this.ratingAvatarThumbs = (Array.isArray(payload.reviews) ? payload.reviews : [])
        .flatMap((review) => (Array.isArray(review.media) ? review.media : []))
        .map((media) => this.getMediaThumbUrl(media))
        .filter(Boolean);
      this.renderRatingAvatars();
    } catch (error) {
      console.warn('[Blanc Space] Loox rating avatars unavailable.', error);
    }
  }

  renderRatingAvatars() {
    if (!this.ratingAvatarButtons?.length) return;
    const thumbs = (this.viewerItems.length
      ? this.viewerItems.map((item) => this.getMediaThumbUrl(item.media))
      : this.ratingAvatarThumbs || []).filter(Boolean).slice(0, 3);
    this.ratingAvatarButtons.forEach((button) => {
      button.replaceChildren();
      thumbs.forEach((src) => {
        const image = document.createElement('img');
        image.src = this.smallLooxUrl(image, src);
        image.alt = '';
        image.decoding = 'async';
        button.append(image);
      });
      button.hidden = thumbs.length === 0;
    });
  }

  renderSummaryAvatars() {
    if (!this.summaryAvatars) return;
    this.summaryAvatars.replaceChildren();
    this.viewerItems.slice(0, 3).forEach((item) => {
      const image = document.createElement('img');
      image.src = this.smallLooxUrl(image, item.media.type === 'video' ? (item.media.thumbnailUrl || item.media.url) : item.media.url);
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      this.summaryAvatars.append(image);
    });
    this.summaryAvatars.hidden = this.summaryAvatars.children.length === 0;
  }

  createReviewCard(review) {
    const article = this.createElement('article', 'loox-api-review-card');
    article.dataset.reviewId = String(review.id);
    const media = Array.isArray(review.media) ? review.media[0] : null;
    if (media?.url) {
      article.classList.add('loox-api-review-card--media');
      const mediaGrid = this.createElement('div', 'loox-api-review-card__media');
      mediaGrid.append(this.createMediaButton(media, review, 0));
      article.append(mediaGrid);
    }

    const content = this.createElement('div', 'loox-api-review-card__content');
    const author = this.createElement('div', 'loox-api-review-card__author');
    const reviewerName = review.reviewer?.name || 'Blanc Space customer';
    const authorDetails = this.createElement('div');
    authorDetails.append(this.createElement('div', 'loox-api-review-card__name', reviewerName));
    if (review.verified) authorDetails.append(this.createVerifiedBadge());
    author.append(authorDetails);
    content.append(author);

    const top = this.createElement('div', 'loox-api-review-card__top');
    const stars = this.createElement('span', 'loox-api-review-card__stars', '★'.repeat(this.clampRating(review.rating)));
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${this.clampRating(review.rating)} out of 5 stars`);
    top.append(stars);

    if (review.date) {
      const time = this.createElement('time', 'loox-api-review-card__date', this.formatDate(review.date));
      time.dateTime = review.date;
      top.append(time);
    }
    content.append(top);

    if (review.body) content.append(this.createReviewBody(review, 'loox-api-review-card__body'));

    if (Array.isArray(review.customQuestions) && review.customQuestions.length) {
      const questions = this.createElement('div', 'loox-api-review-card__questions');
      review.customQuestions.forEach((item) => {
        if (!item?.answer) return;
        questions.append(this.createElement('span', 'loox-api-review-card__question', `${item.question}: ${item.answer}`));
      });
      if (questions.children.length) content.append(questions);
    }

    if (review.product && String(review.product.id || '') !== String(this.productId)) {
      content.append(this.createProductReference(review.product));
    }

    if (review.reply?.body) {
      const reply = this.createElement('div', 'loox-api-review-card__reply');
      reply.append(this.createElement('strong', '', 'Reply from Blanc Space'));
      reply.append(document.createTextNode(review.reply.body));
      content.append(reply);
    }

    article.append(content);
    return article;
  }

  createImagePlaceholder(className) {
    const placeholder = this.createElement('span', className);
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.innerHTML = '<svg viewBox="0 0 32 32" fill="none"><rect x="9" y="3.5" width="14" height="25" rx="3" stroke="currentColor" stroke-width="2"/><circle cx="16" cy="24.5" r="1" fill="currentColor"/><path d="m11.5 18 3.4-3.5 2.6 2.4 2.8-3.1 1.7 1.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return placeholder;
  }

  createProductReference(product, className = 'loox-api-review-card__product') {
    const reference = this.createElement(product.url ? 'a' : 'div', className);
    if (product.url) reference.href = product.url;

    const imageFrame = this.createElement('span', 'loox-api-review-card__product-image');
    imageFrame.append(this.createImagePlaceholder('loox-api-review-card__image-placeholder'));
    if (product.imageUrl) {
      const image = document.createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('load', () => imageFrame.classList.add('is-loaded'), { once: true });
      image.addEventListener('error', () => image.remove(), { once: true });
      image.src = product.imageUrl;
      imageFrame.append(image);
    }
    reference.append(imageFrame);

    const text = this.createElement('span');
    text.append(this.createElement('small', '', 'Review for'));
    text.append(this.createElement('strong', '', product.name || 'a Blanc Space product'));
    reference.append(text);
    return reference;
  }

  createMediaButton(media, review, index) {
    const button = this.createElement('button', 'loox-api-review-card__media-button');
    button.type = 'button';
    button.dataset.reviewMedia = '';
    button.dataset.viewerKey = `${review.id}:${index}`;
    button.dataset.mediaType = media.type === 'video' ? 'video' : 'image';
    if (media.type === 'video' && media.url) button.dataset.videoUrl = this.getPlayableVideoUrl(media.url);
    button.setAttribute('aria-label', `Open ${media.type === 'video' ? 'video' : 'photo'} from ${review.reviewer?.name || 'customer'} review`);

    const previewUrl = media.type === 'video' ? (media.thumbnailUrl || media.url) : media.url;
    button.append(this.createImagePlaceholder('loox-api-review-card__media-placeholder'));
    if (previewUrl) {
      const image = document.createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      const smallUrl = this.smallLooxUrl(null, previewUrl);
      if (smallUrl !== previewUrl) {
        // Base variant missing: fall back to the original before giving up.
        image.addEventListener('error', () => {
          if (image.src !== previewUrl) { image.src = previewUrl; return; }
          image.remove();
        });
      } else {
        image.addEventListener('error', () => image.remove(), { once: true });
      }
      image.src = smallUrl;
      button.append(image);
    }

    if (media.type === 'video') button.append(this.createPlayBadge());
    return button;
  }

  createPlayBadge() {
    const play = this.createElement('span', 'loox-api-review-card__play');
    play.setAttribute('aria-hidden', 'true');
    play.innerHTML = '<svg viewBox="0 0 20 20"><path d="m7 5 8 5-8 5Z" fill="currentColor"/></svg>';
    return play;
  }

  createVerifiedBadge() {
    const badge = this.createElement('span', 'loox-api-review-card__verified');
    badge.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.8 12 4l2.9-.1.5 2.8L18 8l-1.2 2.6.9 2.7-2.7 1-.7 2.8-2.8-.3L10 19l-1.5-2.2-2.8.3-.7-2.8-2.7-1 .9-2.7L2 8l2.6-1.3.5-2.8L8 4Z" fill="currentColor"/><path d="m6.7 10.1 2 2 4.6-4.5" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Verified purchase</span>';
    return badge;
  }

  createReviewBody(review, className) {
    const paragraph = this.createElement('p', className);
    const body = review.body || '';
    const highlights = this.getReviewHighlights(review);
    if (!highlights.length) {
      paragraph.textContent = body;
      return paragraph;
    }

    const ranges = highlights.map((highlight) => {
      const start = body.toLocaleLowerCase(this.locale).indexOf(highlight.toLocaleLowerCase(this.locale));
      return { start, end: start + highlight.length };
    }).filter((range) => range.start >= 0).sort((a, b) => a.start - b.start).filter((range, index, rangesToCheck) => {
      return index === 0 || range.start >= rangesToCheck[index - 1].end;
    });

    if (!ranges.length) {
      paragraph.textContent = body;
      return paragraph;
    }

    let cursor = 0;
    ranges.forEach((range, index) => {
      if (range.start > cursor) paragraph.append(document.createTextNode(body.slice(cursor, range.start)));
      const mark = this.createElement('mark', 'loox-api-review-card__highlight', body.slice(range.start, range.end));
      mark.style.setProperty('--highlight-delay', `${120 + index * 140}ms`);
      paragraph.append(mark);
      cursor = range.end;
    });
    if (cursor < body.length) paragraph.append(document.createTextNode(body.slice(cursor)));
    return paragraph;
  }

  getReviewHighlights(review) {
    const body = review.body || '';
    const apiHighlights = Array.isArray(review.highlights) ? review.highlights.map((highlight) => {
      if (typeof highlight === 'string') return highlight;
      return highlight?.text || highlight?.value || '';
    }).filter(Boolean) : [];
    const matchedApiHighlights = apiHighlights.filter((highlight) => body.toLocaleLowerCase(this.locale).includes(highlight.toLocaleLowerCase(this.locale)));
    if (matchedApiHighlights.length) return matchedApiHighlights.slice(0, 2);

    // The Storefront API documents highlights but the current Loox payloads do
    // not include them. This deterministic fallback emphasises a single
    // concrete benefit phrase from the customer's own words; it does not invent
    // copy or claim to be Loox's AI selection.
    const benefitTerms = /\b(quality|fit|fits|fitted|protect|protective|protection|design|colou?r|print|delivery|value|price|case)\b/gi;
    const positiveTerms = /\b(perfect|perfectly|premium|amazing|amazingly|excellent|beautiful|vibrant|solid|sturdy|love|loved|recommend|great|good|sharp|unreal|durable|strong)\b/gi;
    const proofTerms = /\b(drop|dropped|fell|fall|survived|withstood|held up|repeat|again|third|3rd)\b/gi;
    const sentences = body.match(/[^.!?]+[.!?]?/g)?.map((sentence) => sentence.trim()).filter(Boolean) || [];
    const candidates = [];

    sentences.forEach((sentence) => {
      const parts = sentence.split(/(?:,|;|\s[—–-]\s|\s+and\s+)/i).map((part) => part.trim()).filter(Boolean);
      [...parts, sentence].forEach((part) => {
        const wordCount = part.split(/\s+/).length;
        if (wordCount < 3 || wordCount > 20) return;
        const benefitScore = (part.match(benefitTerms) || []).length;
        const positiveScore = (part.match(positiveTerms) || []).length * 2;
        const proofScore = (part.match(proofTerms) || []).length * 2;
        const score = benefitScore + positiveScore + proofScore;
        if (score >= 3) candidates.push({ text: part, score, wordCount });
      });
    });

    candidates.sort((a, b) => b.score - a.score || a.wordCount - b.wordCount);
    return candidates[0] ? [candidates[0].text] : [];
  }

  layoutReviewCard(card) {
    if (!this.grid || !card?.isConnected) return;
    const gridStyle = getComputedStyle(this.grid);
    const rowHeight = Number.parseFloat(gridStyle.gridAutoRows);
    const rowGap = Number.parseFloat(gridStyle.rowGap);
    if (!rowHeight) return;
    const cardHeight = card.getBoundingClientRect().height;
    card.style.gridRowEnd = `span ${Math.ceil((cardHeight + rowGap) / (rowHeight + rowGap))}`;
  }

  buildViewer() {
    if (this.viewerBuilt || !this.viewerRail || !this.viewerTrack) return;

    this.viewerItems.forEach((item, index) => {
      const reviewerName = item.review.reviewer?.name || 'Blanc Space customer';
      const previewUrl = item.media.type === 'video' ? (item.media.thumbnailUrl || '') : item.media.url;
      const thumb = this.createElement('button', 'loox-api-reviews__viewer-thumb');
      thumb.type = 'button';
      thumb.dataset.reviewViewerSelect = String(index);
      thumb.setAttribute('role', 'tab');
      thumb.setAttribute('aria-selected', 'false');
      thumb.setAttribute('aria-label', `${reviewerName} review ${index + 1}`);
      if (previewUrl) {
        const image = document.createElement('img');
        image.src = this.smallLooxUrl(image, previewUrl);
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        thumb.append(image);
      }
      if (item.media.type === 'video') thumb.append(this.createPlayBadge());
      this.viewerRail.append(thumb);

      const slide = this.createElement('article', 'loox-api-reviews__viewer-slide');
      slide.dataset.reviewViewerSlide = String(index);
      slide.setAttribute('role', 'tabpanel');
      slide.setAttribute('aria-label', `${reviewerName} review ${index + 1}`);
      slide.setAttribute('aria-hidden', 'true');

      if (item.media.type === 'video') {
        const video = document.createElement('video');
        video.className = 'loox-api-reviews__viewer-media';
        video.dataset.reviewViewerVideo = '';
        video.dataset.src = this.getPlayableVideoUrl(item.media.url);
        video.poster = item.media.thumbnailUrl || '';
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'none';
        video.addEventListener('play', () => this.updatePlaybackButton());
        video.addEventListener('pause', () => this.updatePlaybackButton());
        slide.append(video);
      } else {
        const image = document.createElement('img');
        image.className = 'loox-api-reviews__viewer-media';
        image.src = item.media.url;
        image.alt = `Photo shared by ${reviewerName}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        slide.append(image);
      }

      slide.append(this.createViewerReviewBubble(item.review));
      this.viewerTrack.append(slide);
    });

    this.viewerBuilt = true;
  }

  createViewerReviewBubble(review) {
    const bubble = this.createElement('div', 'loox-api-reviews__viewer-bubble');
    const meta = this.createElement('div', 'loox-api-reviews__viewer-meta');
    meta.append(this.createElement('strong', '', review.reviewer?.name || 'Blanc Space customer'));
    if (review.verified) meta.append(this.createVerifiedBadge());
    if (review.date) meta.append(this.createElement('span', 'loox-api-reviews__viewer-date', this.formatDate(review.date)));
    bubble.append(meta);

    const stars = this.createElement('span', 'loox-api-review-card__stars', '★'.repeat(this.clampRating(review.rating)));
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${this.clampRating(review.rating)} out of 5 stars`);
    bubble.append(stars);
    if (review.body) bubble.append(this.createReviewBody(review, 'loox-api-reviews__viewer-copy'));
    if (review.product && String(review.product.id || '') !== String(this.productId)) {
      bubble.append(this.createProductReference(review.product, 'loox-api-reviews__viewer-product'));
    }
    return bubble;
  }

  openViewer(key, trigger) {
    if (!this.viewerItems.length) return;
    const sharedViewer = document.querySelector('video-feedback[data-review-continuation]');
    if (sharedViewer) {
      // Wait for the element upgrade rather than falling through: the local
      // lightbox below renders the WHOLE pool into a dialog and is exactly
      // the pattern that kills the tab on iOS. Shared viewer or nothing.
      const openShared = () => {
        sharedViewer.setReviewItems?.(this.getSharedViewerItems(), this);
        sharedViewer.openReview?.(key, trigger);
      };
      if (sharedViewer.openReview) openShared();
      else customElements.whenDefined('video-feedback').then(openShared);
      return;
    }
    if (!this.lightbox) return;
    const index = Math.max(0, this.viewerItems.findIndex((item) => item.key === key));
    this.buildViewer();
    this.lastViewerTrigger = trigger;
    this.lightbox.showModal();
    document.documentElement.classList.add('loox-review-viewer-open');
    requestAnimationFrame(() => this.selectViewer(index, { behavior: 'auto' }));
  }

  selectViewer(index, { scroll = true, behavior = 'auto' } = {}) {
    if (!this.viewerItems.length) return;
    const nextIndex = Math.min(this.viewerItems.length - 1, Math.max(0, index));
    this.viewerIndex = nextIndex;

    this.querySelectorAll('[data-review-viewer-select]').forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-selected', String(thumbIndex === nextIndex));
      if (thumbIndex === nextIndex) thumb.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    });
    this.querySelectorAll('[data-review-viewer-slide]').forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== nextIndex));
    });

    if (scroll && this.viewerTrack) {
      this.viewerTrack.scrollTo({ left: this.viewerTrack.clientWidth * nextIndex, behavior });
    }

    if (this.viewerPrev) this.viewerPrev.disabled = nextIndex === 0;
    if (this.viewerNext) this.viewerNext.disabled = nextIndex === this.viewerItems.length - 1;

    this.pauseViewerVideos();
    const activeVideo = this.getActiveVideo();
    if (this.videoControls) this.videoControls.hidden = !activeVideo;
    if (activeVideo) {
      if (!activeVideo.src) {
        activeVideo.src = activeVideo.dataset.src;
        activeVideo.load();
      }
      activeVideo.muted = true;
      const play = () => {
        if (activeVideo !== this.getActiveVideo() || !this.lightbox?.open) return;
        activeVideo.play().catch(() => this.updatePlaybackButton());
      };
      if (activeVideo.readyState >= 2) play();
      else activeVideo.addEventListener('canplay', play, { once: true });
    }
    this.updateMuteButton();
    this.updatePlaybackButton();
    requestAnimationFrame(() => this.positionViewerControls());
  }

  positionViewerControls() {
    if (!this.viewerPlayer) return;
    const bubble = this.querySelector(`[data-review-viewer-slide="${this.viewerIndex}"] .loox-api-reviews__viewer-bubble`);
    this.viewerPlayer.style.setProperty('--review-bubble-height', `${bubble?.offsetHeight || 0}px`);
  }

  getActiveVideo() {
    return this.querySelector(`[data-review-viewer-slide="${this.viewerIndex}"] [data-review-viewer-video]`);
  }

  pauseViewerVideos() {
    this.querySelectorAll('[data-review-viewer-video]').forEach((video) => video.pause());
  }

  toggleViewerMute() {
    const video = this.getActiveVideo();
    if (!video) return;
    video.muted = !video.muted;
    this.updateMuteButton();
  }

  toggleViewerPlayback() {
    const video = this.getActiveVideo();
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
    this.updatePlaybackButton();
  }

  updateMuteButton() {
    const video = this.getActiveVideo();
    const muted = !video || video.muted;
    if (this.muteButton) {
      this.muteButton.setAttribute('aria-pressed', String(muted));
      this.muteButton.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }
    const mutedIcon = this.querySelector('[data-review-muted-icon]');
    const soundIcon = this.querySelector('[data-review-sound-icon]');
    if (mutedIcon) mutedIcon.hidden = !muted;
    if (soundIcon) soundIcon.hidden = muted;
  }

  updatePlaybackButton() {
    const video = this.getActiveVideo();
    const playing = Boolean(video && !video.paused);
    if (this.playButton) {
      this.playButton.setAttribute('aria-pressed', String(playing));
      this.playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }
    const pauseIcon = this.querySelector('[data-review-pause-icon]');
    const playIcon = this.querySelector('[data-review-play-icon]');
    if (pauseIcon) pauseIcon.hidden = !playing;
    if (playIcon) playIcon.hidden = playing;
  }

  renderEmptyState() {
    const heading = this.empty?.querySelector('strong');
    const message = this.empty?.querySelector('span');
    if (!heading || !message) return;

    heading.textContent = 'No reviews with photos or videos yet';
    message.textContent = 'Please check back soon for more customer media.';
  }

  setLoading(isLoading, message = '') {
    this.status.hidden = !isLoading;
    if (message) this.status.lastElementChild.textContent = message;
  }

  getResultLabel(total) {
    return `${this.formatNumber(total)} customer ${total === 1 ? 'review' : 'reviews'}`;
  }

  formatNumber(value) {
    return new Intl.NumberFormat(this.locale).format(Number(value) || 0);
  }

  formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(this.locale, { month: 'short', year: 'numeric' }).format(date);
  }

  clampRating(value) {
    return Math.min(5, Math.max(1, Number.parseInt(value, 10) || 1));
  }

  createElement(tagName, className = '', text = '') {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  }
}

if (!customElements.get('loox-api-reviews')) customElements.define('loox-api-reviews', LooxApiReviews);
